import { useMemo, useState, memo } from "react";
import { format, parseISO } from "date-fns";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/lib/currency";
import { describeTermsCode, lookupTermsCode } from "@/lib/par/termsCodeMap";
import { parsePaymentTerms } from "@/lib/backtest/paymentTermsParser";
import { daysBetween } from "@/lib/par/dateUtils";
import { ArrowUpDown, Bell, CheckSquare, CheckCircle2, Pencil, Download } from "lucide-react";
import type { PARInvoiceRow } from "@/lib/par/csvInvoiceParser";
import { TablePagination } from "@/components/par/TablePagination";
import {
  OutstandingFilters,
  type OutstandingFiltersState,
} from "@/components/par/OutstandingFilters";
import { supabase } from "@/integrations/supabase/client";
import { exportOutstandingInvoicesCsv } from "@/lib/csvExport";

interface OutstandingInvoiceTableProps {
  invoices: PARInvoiceRow[];
  referenceDate?: string;
  onOpenNotifications?: () => void;
  onMarkPaid?: (dbIds: string[]) => Promise<void>;
  loading?: boolean;
}

const fmtDate = (d: string | undefined | null): string => {
  if (!d) return "—";
  try {
    return format(parseISO(d), "MMM d, yyyy");
  } catch {
    return d;
  }
};

type SortField = "due" | "amount" | "early" | "invoiced" | "savings" | "effective";
type SortDir = "asc" | "desc";

interface DerivedRow extends PARInvoiceRow {
  outstanding: number;
  daysUntilDue: number | null;
  isOverdue: boolean;
  daysUntilEarly: number | null;
  earlyPayExpired: boolean;
  hasEarlyPay: boolean;
}

interface AggregatedRow extends DerivedRow {
  rowCount: number; // how many source rows were aggregated into this entry
  allDbIds: string[]; // db_ids of all constituent rows (for mark-as-paid)
}

const PAGE_SIZE = 50;

/**
 * Recompute discount_pct, potential_savings, and early_pay_due_date from a new terms string.
 * Uses the same logic as csvInvoiceParser.ts.
 */
function recomputeTermsFields(
  newTerms: string,
  amountInvoiced: number,
  invoicedDate: string
): {
  discount_pct: number | null;
  potential_savings: number;
  early_pay_due_date: string;
} {
  const mappedTerms = lookupTermsCode(newTerms);
  const parsedTerms = mappedTerms ? null : parsePaymentTerms(newTerms);

  const discountPct = mappedTerms?.discountPct ?? parsedTerms?.discountPct ?? null;
  const discountDays = mappedTerms?.discountDays ?? parsedTerms?.discountDays ?? null;

  let earlyPayDueDate = "";
  if (invoicedDate && discountDays != null) {
    try {
      const d = new Date(invoicedDate);
      d.setDate(d.getDate() + discountDays);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      earlyPayDueDate = `${y}-${m}-${day}`;
    } catch {
      earlyPayDueDate = "";
    }
  }

  const potentialSavings = discountPct != null ? (amountInvoiced * discountPct) / 100 : 0;

  return {
    discount_pct: discountPct,
    potential_savings: potentialSavings,
    early_pay_due_date: earlyPayDueDate,
  };
}

export const OutstandingInvoiceTable = memo(function OutstandingInvoiceTable({
  invoices,
  referenceDate,
  onOpenNotifications,
  onMarkPaid,
  loading,
}: OutstandingInvoiceTableProps) {
  const [sort, setSort] = useState<{ field: SortField; dir: SortDir }>({
    field: "effective",
    dir: "asc",
  });
  const [filters, setFilters] = useState<OutstandingFiltersState>({
    search: "",
    vendors: [],
    dueWithinDays: null,
    showEarlyPayOnly: false,
  });
  const [page, setPage] = useState(0);
  const [selectedDbIds, setSelectedDbIds] = useState<Set<string>>(new Set());
  const [markingPaid, setMarkingPaid] = useState(false);
  const [markingPaidId, setMarkingPaidId] = useState<string | null>(null);

  // --- Terms override state ---
  // Local overrides for terms (keyed by db_id): holds the updated PARInvoiceRow fields
  const [termsOverrides, setTermsOverrides] = useState<Map<string, Partial<PARInvoiceRow>>>(
    new Map()
  );
  // Track which rows have been edited this session (for "Edited" badge)
  const [editedDbIds, setEditedDbIds] = useState<Set<string>>(new Set());
  // Track which popover is open (by db_id)
  const [editingDbId, setEditingDbId] = useState<string | null>(null);
  // Draft input value while editing
  const [editDraft, setEditDraft] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  const today = referenceDate || new Date().toISOString().split("T")[0];

  // Merge overrides into invoice list
  const mergedInvoices = useMemo(() => {
    if (termsOverrides.size === 0) return invoices;
    return invoices.map((inv) => {
      if (!inv.db_id) return inv;
      const override = termsOverrides.get(inv.db_id);
      if (!override) return inv;
      return { ...inv, ...override };
    });
  }, [invoices, termsOverrides]);

  const rows = useMemo(() => {
    const result: DerivedRow[] = [];
    for (let i = 0; i < mergedInvoices.length; i++) {
      const inv = mergedInvoices[i];
      const outstanding = inv.amount_invoiced - inv.amount_paid;
      const daysUntilDue = inv.due_date ? daysBetween(inv.due_date, today) : null;
      const isOverdue = daysUntilDue != null && daysUntilDue < 0;

      const daysUntilEarly = inv.early_pay_due_date
        ? daysBetween(inv.early_pay_due_date, today)
        : null;
      const earlyPayExpired = daysUntilEarly != null && daysUntilEarly < 0;
      const hasEarlyPay = inv.discount_pct != null && inv.discount_pct > 0;

      result.push({
        ...inv,
        outstanding,
        daysUntilDue,
        isOverdue,
        daysUntilEarly,
        earlyPayExpired,
        hasEarlyPay,
      });
    }
    return result;
  }, [mergedInvoices, today]);

  const aggregated = useMemo((): AggregatedRow[] => {
    const groups = new Map<string, DerivedRow[]>();
    for (const r of rows) {
      const key = `${r.vendor_name}|${r.invoice_number ?? ""}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(r);
    }
    const result: AggregatedRow[] = [];
    for (const groupRows of groups.values()) {
      const first = groupRows[0];
      const totalOutstanding = groupRows.reduce((s, r) => s + r.outstanding, 0);
      const totalSavings = groupRows.reduce((s, r) => s + r.potential_savings, 0);
      const earlyDates = groupRows
        .map((r) => r.early_pay_due_date)
        .filter(Boolean)
        .sort() as string[];
      const minEarlyDate = earlyDates[0] ?? null;
      const daysUntilEarly = minEarlyDate ? daysBetween(minEarlyDate, today) : null;
      const earlyPayExpired = daysUntilEarly != null && daysUntilEarly < 0;
      const hasEarlyPay = first.discount_pct != null && first.discount_pct > 0;
      const allDbIds = groupRows.map((r) => r.db_id).filter(Boolean) as string[];
      result.push({
        ...first,
        outstanding: totalOutstanding,
        potential_savings: totalSavings,
        early_pay_due_date: minEarlyDate,
        daysUntilEarly,
        earlyPayExpired,
        hasEarlyPay,
        db_id: allDbIds[0] ?? first.db_id,
        rowCount: groupRows.length,
        allDbIds,
      });
    }
    return result;
  }, [rows, today]);

  const vendorList = useMemo(() => {
    const set = new Set<string>();
    for (const r of aggregated) set.add(r.vendor_name);
    return Array.from(set).sort();
  }, [aggregated]);

  const filtered = useMemo(() => {
    let list = aggregated;
    if (filters.showEarlyPayOnly) {
      list = list.filter((r) => r.hasEarlyPay && !r.earlyPayExpired);
    }
    if (filters.search) {
      const q = filters.search.toLowerCase();
      list = list.filter(
        (r) =>
          r.vendor_name.toLowerCase().includes(q) ||
          (r.invoice_number && r.invoice_number.toLowerCase().includes(q))
      );
    }
    if (filters.vendors.length > 0) {
      const vset = new Set(filters.vendors);
      list = list.filter((r) => vset.has(r.vendor_name));
    }
    if (filters.dueWithinDays != null) {
      const max = filters.dueWithinDays;
      list = list.filter(
        (r) => r.daysUntilDue != null && r.daysUntilDue >= 0 && r.daysUntilDue <= max
      );
    }
    return list;
  }, [aggregated, filters]);

  const filteredLen = filtered.length;
  const safePage = useMemo(() => {
    const maxPage = Math.max(0, Math.ceil(filteredLen / PAGE_SIZE) - 1);
    return Math.min(page, maxPage);
  }, [page, filteredLen]);

  const totalOutstanding = aggregated.reduce((s, r) => s + r.outstanding, 0);
  const earlyPayAvailable = aggregated.filter((r) => r.hasEarlyPay && !r.earlyPayExpired);
  const earlyPaySavings = earlyPayAvailable.reduce((s, r) => s + r.potential_savings, 0);
  const earlyPayAmount = earlyPayAvailable.reduce((s, r) => s + r.outstanding, 0);

  const expiringSoon = earlyPayAvailable.filter(
    (r) => r.daysUntilEarly != null && r.daysUntilEarly <= 3
  );
  const expiringSoonSavings = expiringSoon.reduce((s, r) => s + r.potential_savings, 0);

  const sorted = useMemo(() => {
    const { field, dir } = sort;
    const mult = dir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      switch (field) {
        case "effective": {
          const effectiveDeadline = (r: AggregatedRow): number => {
            const due = r.daysUntilDue ?? 9999;
            if (r.daysUntilEarly != null && !r.earlyPayExpired) {
              return Math.min(r.daysUntilEarly, due);
            }
            return due;
          };
          return mult * (effectiveDeadline(a) - effectiveDeadline(b));
        }
        case "early":
          return mult * ((a.daysUntilEarly ?? 9999) - (b.daysUntilEarly ?? 9999));
        case "invoiced":
          return mult * (a.invoiced_date || "").localeCompare(b.invoiced_date || "");
        case "due":
          return mult * (a.due_date || "").localeCompare(b.due_date || "");
        case "amount":
          return mult * (a.outstanding - b.outstanding);
        case "savings":
          return mult * (a.potential_savings - b.potential_savings);
        default:
          return 0;
      }
    });
  }, [filtered, sort]);

  // --- Terms override handlers ---
  const handleOpenEdit = (dbId: string, currentTerms: string) => {
    setEditingDbId(dbId);
    setEditDraft(currentTerms);
  };

  const handleCancelEdit = () => {
    setEditingDbId(null);
    setEditDraft("");
  };

  const handleSaveEdit = async (row: AggregatedRow) => {
    const dbId = row.db_id;
    if (!dbId) return;
    const newTerms = editDraft.trim();
    if (!newTerms) return;

    setEditSaving(true);

    // Recompute derived fields from the new terms string
    const recomputed = recomputeTermsFields(newTerms, row.amount_invoiced, row.invoiced_date);
    const optimisticOverride: Partial<PARInvoiceRow> = {
      terms: newTerms,
      ...recomputed,
    };

    // Optimistically apply override immediately to all constituent rows
    setTermsOverrides((prev) => {
      const next = new Map(prev);
      for (const id of row.allDbIds) {
        next.set(id, optimisticOverride);
      }
      return next;
    });
    setEditedDbIds((prev) => {
      const next = new Set(prev);
      for (const id of row.allDbIds) next.add(id);
      return next;
    });
    setEditingDbId(null);
    setEditDraft("");

    // Persist to Supabase — update all constituent rows for this invoice group
    try {
      for (const id of row.allDbIds) {
        await supabase
          .from("par_invoices")
          .update({
            terms: newTerms,
            discount_pct: recomputed.discount_pct,
            potential_savings: recomputed.potential_savings,
            early_pay_due_date: recomputed.early_pay_due_date || null,
          })
          .eq("id", id);
      }
    } catch {
      // Non-critical: optimistic update is already applied; DB errors are silently ignored
    } finally {
      setEditSaving(false);
    }
  };

  // Show skeleton when loading with no data yet
  if (loading && invoices.length === 0) {
    return (
      <div className="space-y-3">
        <Card className="overflow-hidden">
          <div className="overflow-auto">
            <Table className="table-fixed">
              <colgroup>
                <col style={{ width: "36px" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "7%" }} />
              </colgroup>
              <TableHeader>
                <TableRow className="[&_th]:py-1.5 [&_th]:px-2 [&_th]:text-left">
                  <TableHead className="w-9" />
                  <TableHead>Vendor</TableHead>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Invoiced</TableHead>
                  <TableHead className="text-center">Total Amount</TableHead>
                  <TableHead className="text-center">Discounted Amt</TableHead>
                  <TableHead>Due</TableHead>
                  <TableHead>Early Pay</TableHead>
                  <TableHead>Invoiced Terms</TableHead>
                  <TableHead className="text-right">Potential Savings</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i} className="[&_td]:py-1.5 [&_td]:px-2 h-8">
                    <TableCell>
                      <Skeleton className="h-3.5 w-3.5" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                    <TableCell />
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
    );
  }
  const toggleSort = (field: SortField) => {
    setSort((prev) =>
      prev.field === field
        ? { field, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { field, dir: field === "amount" || field === "savings" ? "desc" : "asc" }
    );
  };

  const SortHeader = ({
    field,
    children,
    className,
  }: {
    field: SortField;
    children: React.ReactNode;
    className?: string;
  }) => (
    <TableHead
      className={cn(
        "text-xs font-semibold cursor-pointer select-none hover:text-foreground transition-colors text-left",
        className
      )}
      onClick={() => toggleSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        <ArrowUpDown
          className={cn(
            "w-3 h-3",
            sort.field === field ? "text-primary" : "text-muted-foreground/40"
          )}
        />
      </span>
    </TableHead>
  );

  return (
    <div className="space-y-3">
      {/* Summary — early pay focused */}
      <div className="flex gap-3">
        {/* Primary: early pay action card */}
        <Card
          className={cn(
            "flex-[2] p-4 border-success/30 bg-success/5",
            earlyPayAvailable.length === 0 && "border-muted bg-muted/5"
          )}
        >
          <div>
            <span className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold">
              Early Pay Opportunities
            </span>
          </div>
          <div className="flex items-baseline gap-3 mt-1">
            <p
              className={cn(
                "text-xl font-bold leading-tight",
                earlyPayAvailable.length > 0 ? "text-success" : "text-muted-foreground"
              )}
            >
              {formatCurrency(earlyPaySavings)}
            </p>
            <span className="text-[10px] text-muted-foreground">
              savings on {formatCurrency(earlyPayAmount)} across {earlyPayAvailable.length} invoices
            </span>
          </div>
          {expiringSoon.length > 0 && (
            <p className="text-[10px] text-muted-foreground font-medium mt-0.5">
              <span className="text-amber-400">⚡ {expiringSoon.length}</span> expiring within 3
              days ({formatCurrency(expiringSoonSavings)} at risk)
            </p>
          )}
        </Card>

        <Card className="flex-1 p-4">
          <span className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold">
            Outstanding
          </span>
          <p className="text-xl font-bold text-foreground leading-tight">
            {formatCurrency(totalOutstanding)}
          </p>
          <p className="text-[10px] text-muted-foreground">{aggregated.length} invoices</p>
        </Card>
      </div>

      {/* Bulk action bar */}
      {selectedDbIds.size > 0 && (
        <div className="flex items-center gap-3 px-3 py-2 bg-primary/5 border border-primary/20 rounded-lg">
          <CheckSquare className="w-3 h-3 text-primary" />
          <span className="text-sm font-medium">
            {selectedDbIds.size} invoice{selectedDbIds.size !== 1 ? "s" : ""} selected
          </span>
          <div className="flex items-center gap-2 ml-auto">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => setSelectedDbIds(new Set())}
            >
              Clear
            </Button>
            {onMarkPaid && (
              <Button
                size="sm"
                className="h-7 text-xs gap-1.5"
                disabled={markingPaid}
                onClick={async () => {
                  setMarkingPaid(true);
                  const allIds = sorted
                    .filter((r) => r.db_id && selectedDbIds.has(r.db_id))
                    .flatMap((r) => r.allDbIds);
                  await onMarkPaid(allIds);
                  setSelectedDbIds(new Set());
                  setMarkingPaid(false);
                }}
              >
                Mark as Paid
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Filters row */}
      <div className="flex items-center justify-between gap-2">
        <OutstandingFilters
          vendors={vendorList}
          earlyPayCount={earlyPayAvailable.length}
          filters={filters}
          onChange={(f) => {
            setFilters(f);
            setPage(0);
          }}
        />
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-[11px] gap-1.5 shrink-0"
            onClick={() =>
              exportOutstandingInvoicesCsv(
                sorted.map((r) => ({
                  vendor_name: r.vendor_name,
                  vendor_number: r.vendor_number,
                  invoice_number: r.invoice_number,
                  invoiced_date: r.invoiced_date,
                  due_date: r.due_date,
                  early_pay_due_date: r.early_pay_due_date ?? undefined,
                  outstanding: r.outstanding,
                  potential_savings: r.potential_savings,
                  terms: r.terms,
                  discount_pct: r.discount_pct,
                }))
              )
            }
            disabled={sorted.length === 0}
          >
            <Download className="w-3 h-3" />
            Export CSV
          </Button>
          {onOpenNotifications && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-[11px] gap-1.5 shrink-0"
              onClick={onOpenNotifications}
            >
              <Bell className="w-3 h-3" />
              Notifications
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-auto max-h-[60vh]">
          <Table className="table-fixed">
            <colgroup>
              <col style={{ width: "36px" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "7%" }} />
            </colgroup>
            <TableHeader className="sticky top-0 z-10 bg-card border-b border-border">
              <TableRow className="hover:bg-muted/40 [&_th]:py-1.5 [&_th]:px-2 [&_th]:text-left">
                <TableHead className="w-9">
                  {(() => {
                    const pageRows = sorted
                      .slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE)
                      .filter((r) => r.db_id);
                    const allSelected =
                      pageRows.length > 0 && pageRows.every((r) => selectedDbIds.has(r.db_id!));
                    const someSelected =
                      !allSelected && pageRows.some((r) => selectedDbIds.has(r.db_id!));
                    return (
                      <Checkbox
                        checked={allSelected ? true : someSelected ? "indeterminate" : false}
                        onCheckedChange={(checked) => {
                          setSelectedDbIds((prev) => {
                            const next = new Set(prev);
                            if (checked) pageRows.forEach((r) => next.add(r.db_id!));
                            else pageRows.forEach((r) => next.delete(r.db_id!));
                            return next;
                          });
                        }}
                        className="h-3.5 w-3.5"
                      />
                    );
                  })()}
                </TableHead>
                <TableHead className="text-left">Vendor</TableHead>
                <TableHead className="text-left">Invoice #</TableHead>
                <SortHeader field="invoiced">Invoiced</SortHeader>
                <SortHeader field="amount" className="text-center">
                  Total Amount
                </SortHeader>
                <TableHead className="text-center">Discounted Amt</TableHead>
                <SortHeader field="due">Due</SortHeader>
                <SortHeader field="early">Early Pay</SortHeader>
                <TableHead className="text-left">Invoiced Terms</TableHead>
                <SortHeader field="savings" className="text-right">
                  Potential Savings
                </SortHeader>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={11}
                    className="text-center text-xs text-muted-foreground py-8"
                  >
                    {filtered.length === 0 && filters.showEarlyPayOnly
                      ? "No early pay opportunities available"
                      : "No matching invoices"}
                  </TableCell>
                </TableRow>
              ) : (
                sorted.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE).map((r, i) => {
                  const idx = safePage * PAGE_SIZE + i;
                  const urgencyClass =
                    r.hasEarlyPay && !r.earlyPayExpired
                      ? r.daysUntilEarly != null && r.daysUntilEarly <= 3
                        ? "border-l-warning"
                        : "border-l-success"
                      : r.isOverdue
                        ? "border-l-destructive"
                        : r.daysUntilDue != null && r.daysUntilDue <= 7
                          ? "border-l-warning"
                          : "border-l-transparent";

                  const isEdited = r.db_id ? editedDbIds.has(r.db_id) : false;
                  const isEditingThis = r.db_id ? editingDbId === r.db_id : false;

                  return (
                    <TableRow
                      key={`${r.vendor_name}-${r.invoice_number}-${idx}`}
                      className={cn(
                        "group border-l-3 [&_td]:py-1 [&_td]:px-2 h-8",
                        urgencyClass,
                        r.db_id && selectedDbIds.has(r.db_id) && "bg-primary/5"
                      )}
                    >
                      <TableCell>
                        {r.db_id && (
                          <Checkbox
                            checked={selectedDbIds.has(r.db_id)}
                            onCheckedChange={(checked) => {
                              setSelectedDbIds((prev) => {
                                const next = new Set(prev);
                                if (checked) next.add(r.db_id!);
                                else next.delete(r.db_id!);
                                return next;
                              });
                            }}
                            className={cn(
                              "h-3.5 w-3.5 transition-opacity",
                              selectedDbIds.has(r.db_id)
                                ? "opacity-100"
                                : "opacity-0 group-hover:opacity-100"
                            )}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <p
                          className="text-[11px] font-medium text-foreground truncate"
                          title={r.vendor_name}
                        >
                          {r.vendor_name}
                        </p>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 min-w-0">
                          <p
                            className="text-[10px] text-muted-foreground font-mono truncate"
                            title={r.invoice_number || ""}
                          >
                            {r.invoice_number || "—"}
                          </p>
                          {r.rowCount > 1 && (
                            <span className="text-[8px] bg-muted text-muted-foreground rounded px-1 shrink-0">
                              ×{r.rowCount}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-[10px] text-muted-foreground">
                          {fmtDate(r.invoiced_date)}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-[11px] font-semibold">
                          {formatCurrency(r.outstanding)}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        {r.potential_savings > 0 && !r.earlyPayExpired ? (
                          <span className="text-[11px] font-semibold text-success">
                            {formatCurrency(r.outstanding - r.potential_savings)}
                          </span>
                        ) : (
                          <span className="text-[10px] text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-[10px] text-muted-foreground">
                          {fmtDate(r.due_date)}
                        </span>
                      </TableCell>
                      <TableCell>
                        {r.hasEarlyPay ? (
                          <span className="text-[10px] text-muted-foreground">
                            {fmtDate(r.early_pay_due_date)}
                          </span>
                        ) : (
                          <span className="text-[10px] text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      {/* Invoiced Terms cell with inline edit */}
                      <TableCell>
                        <div className="flex items-center gap-1 min-w-0">
                          {r.terms ? (
                            (() => {
                              const hasDiscount = lookupTermsCode(r.terms)?.discountPct != null;
                              return (
                                <span
                                  className={cn(
                                    "inline-block px-1 py-0 rounded text-[9px] font-medium border leading-tight shrink-0",
                                    hasDiscount
                                      ? "bg-success/10 text-success border-success/20"
                                      : "bg-muted text-muted-foreground border-border"
                                  )}
                                >
                                  {describeTermsCode(r.terms)}
                                </span>
                              );
                            })()
                          ) : (
                            <span className="text-[10px] text-muted-foreground shrink-0">—</span>
                          )}
                          {isEdited && (
                            <Badge
                              variant="outline"
                              className="text-[8px] px-1 py-0 h-3.5 leading-none border-amber-400/50 text-amber-500 bg-amber-400/10 shrink-0"
                            >
                              Edited
                            </Badge>
                          )}
                          {r.db_id && (
                            <Popover
                              open={isEditingThis}
                              onOpenChange={(open) => {
                                if (!open) handleCancelEdit();
                              }}
                            >
                              <PopoverTrigger asChild>
                                <button
                                  className="shrink-0"
                                  onClick={() => handleOpenEdit(r.db_id!, r.terms ?? "")}
                                  title="Edit payment terms"
                                >
                                  <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-60 hover:!opacity-100 ml-1 cursor-pointer text-muted-foreground" />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent className="w-64 p-3" side="bottom" align="start">
                                <div className="space-y-2">
                                  <p className="text-[11px] font-semibold text-foreground">
                                    Override payment terms
                                  </p>
                                  <Input
                                    className="h-7 text-[11px]"
                                    value={editDraft}
                                    onChange={(e) => setEditDraft(e.target.value)}
                                    placeholder="e.g. 210, 2/10 Net 30"
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") handleSaveEdit(r);
                                      if (e.key === "Escape") handleCancelEdit();
                                    }}
                                    autoFocus
                                  />
                                  <p className="text-[10px] text-muted-foreground">
                                    e.g. 2/10 Net 30, 1/15 Net 45
                                  </p>
                                  <div className="flex gap-2 pt-1">
                                    <Button
                                      size="sm"
                                      className="h-6 text-[11px] flex-1"
                                      disabled={editSaving || !editDraft.trim()}
                                      onClick={() => handleSaveEdit(r)}
                                    >
                                      {editSaving ? "Saving…" : "Save"}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 text-[11px] flex-1"
                                      onClick={handleCancelEdit}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                              </PopoverContent>
                            </Popover>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {r.potential_savings > 0 && !r.earlyPayExpired ? (
                          <span className="text-[11px] font-bold text-success">
                            {formatCurrency(r.potential_savings)}
                          </span>
                        ) : r.potential_savings > 0 ? (
                          <span className="text-[10px] text-muted-foreground line-through">
                            {formatCurrency(r.potential_savings)}
                          </span>
                        ) : (
                          <span className="text-[10px] text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {r.db_id && onMarkPaid && (
                          <button
                            className={cn(
                              "opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1 text-[10px] font-medium text-muted-foreground hover:text-success rounded px-1.5 py-0.5 hover:bg-success/10",
                              markingPaidId === r.db_id && "opacity-100"
                            )}
                            disabled={markingPaidId === r.db_id}
                            onClick={async () => {
                              setMarkingPaidId(r.db_id!);
                              await onMarkPaid(r.allDbIds);
                              setMarkingPaidId(null);
                            }}
                          >
                            <CheckCircle2 className="w-3 h-3 shrink-0" />
                            {markingPaidId === r.db_id ? "…" : "Paid"}
                          </button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
      <TablePagination
        page={safePage}
        pageSize={PAGE_SIZE}
        total={sorted.length}
        onPageChange={setPage}
      />
    </div>
  );
});
